import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getWishlist, removeFromWishlist } from '../../services/wishlistService';
import { useCart } from '../../contexts/CartContext';
import { getShopByName, convertShopNameToUrl } from '../../services/shopService';
import Toast from '../../components/Toast';

const Wishlist = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addToCart } = useCart();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [shopsData, setShopsData] = useState({});

  useEffect(() => {
    const loadWishlist = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const items = await getWishlist(currentUser.uid);
        
        // Filtra gli elementi che hanno uno shopId valido
        const validItems = items.filter(item => item.shopId && item.shopName);
        setWishlistItems(validItems);

        // Carica i dati dei negozi solo per gli elementi validi
        const uniqueShopIds = [...new Set(validItems.map(item => item.shopId))];
        const shopsDataPromises = uniqueShopIds.map(async (shopId) => {
          try {
            const shopData = await getShopByName(shopId);
            return [shopId, shopData];
          } catch (err) {
            console.error(`Errore nel caricamento del negozio ${shopId}:`, err);
            return [shopId, null];
          }
        });

        const shopsDataArray = await Promise.all(shopsDataPromises);
        const shopsDataObject = Object.fromEntries(shopsDataArray.filter(([_, data]) => data !== null));
        setShopsData(shopsDataObject);

        // Inizializza le quantità solo per gli elementi validi
        const initialQuantities = {};
        validItems.forEach(item => {
          initialQuantities[item.id] = 1;
        });
        setQuantities(initialQuantities);
      } catch (err) {
        console.error('Errore nel caricamento della wishlist:', err);
        setError('Errore nel caricamento della wishlist');
      } finally {
        setLoading(false);
      }
    };

    loadWishlist();
  }, [currentUser]);

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= 99) {
      setQuantities(prev => ({
        ...prev,
        [itemId]: newQuantity
      }));
    }
  };

  const handleRemoveFromWishlist = async (itemId) => {
    try {
      await removeFromWishlist(currentUser.uid, itemId);
      setWishlistItems(prev => prev.filter(item => item.id !== itemId));
      setToastMessage('Articolo rimosso dalla lista dei desideri');
      setToastVariant('success');
      setShowToast(true);
    } catch (err) {
      console.error('Errore nella rimozione dalla wishlist:', err);
      setToastMessage('Errore nella rimozione dalla lista dei desideri');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const handleAddToCart = (item) => {
    if (!item.shopId || !item.shopName) {
      setToastMessage('Errore: dati del negozio non disponibili');
      setToastVariant('danger');
      setShowToast(true);
      return;
    }

    addToCart({
      ...item,
      shopId: item.shopId,
      shopName: item.shopName
    }, quantities[item.id]);

    setToastMessage('L\'articolo è stato aggiunto al carrello');
    setToastVariant('success');
    setShowToast(true);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5 text-danger">
        {error}
      </div>
    );
  }

  return (
    <div className="fade-in">
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage}
        variant={toastVariant}
      />
      <Container fluid className="px-0 position-relative" style={{ minHeight: '100vh' }}>
        <div className="d-flex flex-column align-items-center w-100" style={{ maxWidth: 380, margin: '0 auto' }}>
          {/* Header con pulsante indietro */}
          <div className="w-100 mb-4">
            <Button
              variant="dark"
              className="rounded-circle shadow-sm border-0"
              style={{ width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => navigate('/profile')}
            >
              <i className="bi bi-arrow-left fs-4 text-white"></i>
            </Button>
          </div>

          {/* Titolo */}
          <div className="w-100 mb-4">
            <h4 className="fw-bold mb-0">Lista dei desideri</h4>
            <p className="text-muted mb-0">{wishlistItems.length} prodotti</p>
          </div>

          {/* Lista prodotti */}
          {wishlistItems.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-heart" style={{ fontSize: 48, color: '#e0e0e0' }}></i>
              <p className="text-muted mt-3">La tua lista dei desideri è vuota</p>
            </div>
          ) : (
            <div className="w-100">
              {wishlistItems.map((item) => (
                <Card key={item.id} className="mb-3 border-0 shadow-sm rounded-4">
                  <Card.Body className="p-3">
                    <div className="d-flex">
                      <div className="rounded-4 overflow-hidden bg-white" style={{ width: 80, height: 80 }}>
                        <img 
                          src={item.image} 
                          alt={item.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                      <div className="ms-3 flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1 fw-bold">{item.name}</h6>
                            <p className="text-muted small mb-0">
                              {item.shopName || shopsData[item.shopId]?.name || 'Negozio non disponibile'}
                            </p>
                          </div>
                          <Button
                            variant="link"
                            className="p-0 text-danger"
                            onClick={() => handleRemoveFromWishlist(item.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mt-3">
                          <div className="fw-bold">€{item.price.toFixed(2)}</div>
                          <div style={{ background: '#F2F2F2', borderRadius: 32, display: 'flex', alignItems: 'center', padding: '4px 4px' }}>
                            <Button
                              variant="light"
                              className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                              style={{ width: 32, height: 32, fontSize: 16, padding: 0 }}
                              onClick={() => handleQuantityChange(item.id, quantities[item.id] - 1)}
                              disabled={quantities[item.id] <= 1}
                            >
                              <i className="bi bi-dash"></i>
                            </Button>
                            <span className="mx-2 fw-bold" style={{ fontSize: 14, minWidth: 20, textAlign: 'center' }}>
                              {quantities[item.id]}
                            </span>
                            <Button
                              variant="light"
                              className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                              style={{ width: 32, height: 32, fontSize: 16, padding: 0 }}
                              onClick={() => handleQuantityChange(item.id, quantities[item.id] + 1)}
                              disabled={quantities[item.id] >= 99}
                            >
                              <i className="bi bi-plus"></i>
                            </Button>
                          </div>
                          <Button
                            variant="dark"
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 40, height: 40, padding: 0 }}
                            onClick={() => handleAddToCart(item)}
                          >
                            <i className="bi bi-basket2 fs-5"></i>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Wishlist; 